import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { PoAddress } from '@/lib/po-draft-import-api';
import { addressErrors, addressIsBlank, emptyAddress } from '@/lib/po-draft-utils';

const FIELDS: { k: Extract<keyof PoAddress, string>; label: string; span?: boolean }[] = [
  { k: 'firstName', label: 'First name' }, { k: 'lastName', label: 'Last name' },
  { k: 'company', label: 'Company', span: true }, { k: 'address1', label: 'Address line 1', span: true },
  { k: 'address2', label: 'Address line 2', span: true }, { k: 'city', label: 'City' },
  { k: 'province', label: 'State / province' }, { k: 'zip', label: 'Postcode' },
  { k: 'countryCode', label: 'Country (ISO2)' }, { k: 'phone', label: 'Phone', span: true },
];

export function AddressEditor({ value, onChange, disabled }: { value: PoAddress; onChange: (a: PoAddress) => void; disabled: boolean }) {
  const errs = addressErrors(value);
  return (
    <section className="glass-panel p-4 space-y-3" data-testid="section-shipping">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Shipping recipient</h3>
        <Button size="sm" variant="outline" disabled={disabled || addressIsBlank(value)} onClick={() => onChange(emptyAddress())} data-testid="button-clear-shipping">Clear (pickup / no shipping)</Button>
      </div>
      <p className="text-xs text-muted-foreground">Separate from the billing customer. Leave every field blank for pickup or no shipping address.</p>
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map((f) => (
          <label key={f.k} className={`space-y-1 ${f.span ? 'col-span-2' : ''}`}>
            <span className="text-xs text-muted-foreground">{f.label}</span>
            <Input value={value[f.k]} disabled={disabled} maxLength={f.k === 'countryCode' ? 2 : undefined}
              onChange={(e) => onChange({ ...value, [f.k]: f.k === 'countryCode' ? e.target.value.toUpperCase() : e.target.value })} data-testid={`input-ship-${f.k}`} />
          </label>
        ))}
      </div>
      {addressIsBlank(value) && <p className="text-xs text-muted-foreground" data-testid="text-no-shipping">No shipping address will be set.</p>}
      {errs.length > 0 && <ul className="text-xs text-destructive list-disc pl-4" data-testid="text-ship-errors">{errs.map((e) => <li key={e}>{e}</li>)}</ul>}
    </section>
  );
}
